import React, { useRef, useState } from 'react';
import { AvTooltip } from '@fabric-ui/components';
import { History, Lightning } from '@fabric-ui/icons';
import { flex } from '@fabric-ui/theme';
import { Box, Button, ToggleButton, ToggleButtonGroup } from '@mui/material';
import useClickOutside from '../hooks/useClickOutside';
import { DateCondition } from '../types/filter.types';
import { formatTimeRange } from '../views/CustomDashboards/components/TimeFilter/utils';
import { FilterTimeType } from '../views/Reports/types';
import AvDialog from './AvDialog';
import AvDateRangePicker from './DatePicker/AvDateRangePicker';
import { PeriodBehavior } from './DatePicker/AvDateRangePicker.constants';
import ShortVal from './DatePicker/ShortVal';
import { dateFormat, RelativeUnit } from './DatePicker/utils';
import { getDateFromFilter, getStringConditionFromDatePicker } from './filters/utils';
import { FilterStyledPopper } from './Select/constants';
import SelectTrigger from './Select/SelectTrigger';
import { dateOptionStyle } from './SelectDate';

const lightningIcon = <Lightning />;
const historyIcon = <History />;

export const newDefaultTimeRange: DateCondition = {
  relative: {
    unit: RelativeUnit.MONTHS,
    value: 3,
    periodBehaviour: PeriodBehavior.last,
  },
};

export interface AvHistoricalDatePickerProps {
  isCurrentTime: boolean;
  setIsCurrentTime: (isCurrentTime: boolean) => void;
  timeRange: DateCondition;
  isEditMode?: boolean;
  shouldWarn?: boolean;
  updateFilters: (v: DateCondition) => void;
}
export const AvHistoricalDatePicker: React.FC<AvHistoricalDatePickerProps> = ({
  isCurrentTime,
  setIsCurrentTime,
  timeRange,
  isEditMode = true,
  shouldWarn = false,
  updateFilters,
}) => {
  const dateValue = getDateFromFilter(timeRange);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  const datePickerInputRef = useRef(null);
  const datePickerRef = useRef(null);

  const triggerProps = {
    ref: datePickerInputRef,
    onClick: () => setIsDatePickerOpen(true),
    open: isDatePickerOpen,
    chipCount: undefined,
    label: formatTimeRange(timeRange, dateFormat),
    size: 'xSmall',
    sx: {
      width: '100%',
      px: 0,
      background: 'transparent',
      color: theme => (isEditMode ? theme.palette.colors.neutrals[800] : theme.palette.colors.neutrals[600]),
      fontWeight: 400,
      fontSize: '14px',
      ':hover': {
        background: 'transparent',
      },
      '&.Mui-focusVisible': {
        background: 'transparent',
      },
      ...(!isEditMode ? { pointerEvents: 'none' } : {}),
    },
    showSelection: false,
  } as const;

  const closeDatePicker = () => setIsDatePickerOpen(false);

  useClickOutside(datePickerRef, closeDatePicker);

  const inputSize = 480;
  const onCloseDialog = () => setIsModalOpen(false);

  const footer = (
    <Box sx={{ ...flex.justifyEndCenter, gap: 2 }}>
      <Button onClick={onCloseDialog}>Cancel</Button>
      <Button
        variant="contained"
        onClick={() => {
          setIsCurrentTime(!isCurrentTime);
          setIsModalOpen(false);
        }}>
        Continue
      </Button>
    </Box>
  );

  const currentTimeText = (isCurrentTime: boolean) => (isCurrentTime ? 'Current' : 'Historical');

  return (
    <Box
      sx={{
        ...flex.row,
      }}>
      <AvDialog titleVariant="error" title="Changing Data Time" onClose={onCloseDialog} open={!!isModalOpen} footer={footer}>
        <div>
          <div>
            Changing data time from {currentTimeText(isCurrentTime)} to {currentTimeText(!isCurrentTime)} may discard all selected fields
            and delete all changes on the report
          </div>
          <br />
          <div>Are you sure you want to proceed? </div>
        </div>
      </AvDialog>

      <Box
        sx={{
          ...flex.row,
          gap: 2,
          ml: -1.5,
          border: t => `1px solid ${isDatePickerOpen ? t.palette.colors.primary[400] : t.palette.colors.neutrals[400]}`,
          borderRadius: '10px',
          padding: '3px',
          maxHeight: '32px',
          width: '480px',
          maxWidth: '480px',
          ':hover': {
            ...(!isDatePickerOpen && isEditMode ? { borderColor: 'black' } : {}),
          },
        }}>
        <ToggleButtonGroup
          sx={{ marginRight: '-24px', zIndex: 1, pointerEvents: isEditMode ? 'all' : 'none' }}
          color="white"
          size="xSmall"
          value={isCurrentTime ? FilterTimeType.CURRENT : FilterTimeType.HISTORICAL}
          exclusive
          disabled={!isEditMode}
          onChange={e => {
            e.stopPropagation();
            return shouldWarn ? setIsModalOpen(true) : setIsCurrentTime(!isCurrentTime);
          }}>
          <ToggleButton value={FilterTimeType.CURRENT}>
            <AvTooltip title="Filter On Current Data">
              <Box sx={{ ...flex.center, gap: 1 }}>
                {lightningIcon} {isCurrentTime ? `Current` : ''}
              </Box>
            </AvTooltip>
          </ToggleButton>
          <ToggleButton value={FilterTimeType.HISTORICAL}>
            <AvTooltip title="Filter On History Data">
              <Box sx={{ ...flex.center, gap: 1 }}>
                {historyIcon} {isCurrentTime ? '' : 'History'}
              </Box>
            </AvTooltip>
          </ToggleButton>
        </ToggleButtonGroup>
        <Box
          sx={theme => ({
            ...dateOptionStyle(theme),
            pl: 2,
            alignItems: 'center',
            gap: 1,
            width: '100%',
          })}>
          {!isCurrentTime && timeRange.relative ? <ShortVal unit={timeRange.relative.unit} value={timeRange.relative.value} /> : <span />}
          {isCurrentTime ? (
            <span>Current</span>
          ) : (
            <>
              <SelectTrigger {...triggerProps} />
              <FilterStyledPopper
                open={isDatePickerOpen}
                anchorEl={datePickerInputRef.current}
                style={{ right: '80px', top: '162px', left: 'unset' }}
                transition
                sx={{
                  minWidth: '400px',
                  width: 'fit-content',
                  height: 'fit-content',
                  padding: 0,
                }}>
                <Box ref={datePickerRef}>
                  <AvDateRangePicker
                    showInputIcon={false}
                    showAsCalendar
                    onClose={closeDatePicker}
                    value={dateValue.value}
                    activePresetValue={dateValue.preset}
                    onChange={value => {
                      const newTimeRange = getStringConditionFromDatePicker(value).dateCondition! || newDefaultTimeRange;
                      updateFilters(newTimeRange);
                    }}
                    isEditableInput={false}
                    showRelativeOptions
                    isRange
                    allowFutureDates={false}
                    isFilterExpression
                    showOptions={false}
                    inputSx={{
                      border: '1px solid pink',
                      maxWidth: inputSize,
                      width: inputSize,
                    }}
                  />
                </Box>
              </FilterStyledPopper>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};
